import Layout from "@/components/Layout";
import {
  ArrowRight,
  Clock,
  FileText,
  LifeBuoy,
  Mail,
  ShieldCheck,
  UserRound,
} from "lucide-react";

const SUPPORT_EMAIL = "support@wellnextek.com";

const includeItems = [
  {
    icon: UserRound,
    title: "Who you are",
    description:
      "Your name, the clinic or organisation you represent, and the email or phone number you registered with.",
  },
  {
    icon: FileText,
    title: "What you need help with",
    description:
      "The product (e.g. Prismedic), what you were trying to do, what happened instead, and any screenshots or error messages.",
  },
  {
    icon: Clock,
    title: "When it happened",
    description:
      "The date and approximate time of the issue, and whether it is still happening. This helps us trace it quickly.",
  },
];

const Support = () => {
  return (
    <Layout>
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative py-28 overflow-hidden bg-gradient-to-br from-primary/8 via-background to-secondary/8">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="container mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-8">
            <LifeBuoy className="h-3.5 w-3.5" />
            Wellnext Support
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1]">
            <span className="text-gradient-primary">We're here to help</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-10">
            For product support, account and sign-up assistance, or any other
            enquiry, email our support team and we'll get back to you.
          </p>

          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3 text-base font-semibold text-primary-foreground hover:opacity-90 transition-opacity shadow-[0_4px_14px_rgba(0,0,0,0.12)]"
            data-testid="button-email-support"
          >
            <Mail className="h-5 w-5" />
            {SUPPORT_EMAIL}
          </a>
        </div>
      </section>

      {/* ─── How to reach us ──────────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
            <div className="h-[3px] w-full bg-gradient-to-r from-primary via-secondary to-accent" />
            <div className="p-8 md:p-10">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 shrink-0 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    Email us at{" "}
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="text-primary hover:underline break-all"
                    >
                      {SUPPORT_EMAIL}
                    </a>
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We do not use an online contact form. Please write to us
                    directly from your email client so that we can keep a clear
                    record of the conversation and reply to you on the same
                    thread. Our team typically responds within 24–48 hours on
                    business days.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── What to include ──────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-b from-muted/40 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Help us help you
            </span>
            <h2 className="text-4xl font-bold">
              <span className="text-gradient-primary">What to include in your email</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {includeItems.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="group rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                <div className="h-[3px] w-full bg-gradient-to-r from-primary to-primary/40" />
                <div className="p-8">
                  <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-12 flex items-start gap-3 rounded-2xl border border-border/60 bg-white/70 p-5 text-sm text-muted-foreground">
            <ShieldCheck className="h-5 w-5 shrink-0 text-primary mt-0.5" />
            <p>
              Please do not include patient records or other sensitive clinical
              data in your email. If our team needs it to resolve your issue, we
              will guide you through a secure way to share it.
            </p>
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute right-0 top-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 relative text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Ready when you are</h2>
            <p className="text-muted-foreground mb-8 text-lg">
              Drop us a line and a member of the Wellnext team will take it from
              there.
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3 text-base font-semibold text-primary-foreground hover:opacity-90 transition-opacity shadow-[0_4px_14px_rgba(0,0,0,0.12)]"
            >
              Email Support
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Support;
